/* CustomerOrdersCrudCard.tsx
    Une card affichant la gestion des commandes clients
    Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
    Utilise l'API correspondante pour les opérations de données
*/

import React from 'react';
import { GenericCrudCard } from 'common';
import { CustomerOrderData, customerOrdersApi } from 'features/customerOrders';
import { faBox } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface CustomerOrdersCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
}

// CustomerOrdersCrudCard component
export const CustomerOrdersCrudCard: React.FC<CustomerOrdersCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/CustomerOrders',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [{ label: 'ID', field: 'id', render: (a: CustomerOrderData) => a.id }]
			: []),
		// Colonne "Référence"
		{
			label: 'Référence',
			field: 'referenceNumber',
			render: (a: CustomerOrderData) => a.referenceNumber || '',
		},
		// Colonne "Affaire" — toujours visible (clé métier)
		{
			label: 'Affaire',
			field: 'project.referenceNumber',
			render: (a: CustomerOrderData) =>
				a.project ? a.project.referenceNumber : '—',
			exportValue: (a: CustomerOrderData) => a.project?.referenceNumber ?? '',
			linkUrl: (a: CustomerOrderData) =>
				a.project ? `/app/projects/${a.project.id}/edit` : '',
		},
		// Colonne "Montant HT" — toujours visible
		{
			label: 'Montant HT',
			field: 'amount',
			render: (a: CustomerOrderData) =>
				new Intl.NumberFormat('fr-FR', {
					style: 'currency',
					currency: 'EUR',
				}).format(Number(a.amount ?? 0)),
			exportValue: (a: CustomerOrderData) => Number(a.amount ?? 0),
		},
		// Colonne "Date de commande" au format jour/mois/année
		{
			label: 'Date',
			field: 'orderDate',
			filterType: 'date',
			render: (a: CustomerOrderData) =>
				a.orderDate ? new Date(a.orderDate).toLocaleDateString('fr-FR') : '—',
			exportValue: (a: CustomerOrderData) =>
				a.orderDate ? new Date(a.orderDate).toLocaleDateString('fr-FR') : '',
		},
	];

	return (
		<GenericCrudCard<CustomerOrderData>
			queryKey="customerOrders"
			title={
				size === 'sm' ? 'Commandes clients' : 'Liste des commandes clients'
			}
			fetchData={(token, options) =>
				customerOrdersApi.listPaginated(token, options)
			}
			deleteItem={customerOrdersApi.delete}
			tableColumns={columns}
			addButtonLabel="Ajouter une commande client"
			getAddPath={() => '/app/CustomerOrders/new'}
			getEditPath={(id) => `/app/CustomerOrders/${id}/edit`}
			emptyMessage="Aucune commande client trouvée."
			getReturnPath={hideReturn ? undefined : () => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faBox : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};
export default CustomerOrdersCrudCard;
